import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { fetchProjectById, createProject, updateProject } from '../services/projectService';
import { uploadFile } from '../services/uploadService';
import { Button, Input, TextArea, FileUpload } from '../components/common';
import { getImageUrl } from '../utils/imageUrl';
import AdminLayout from './AdminLayout';

const ProjectForm = () => {
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('');
    const [shortDesc, setShortDesc] = useState('');
    const [longDesc, setLongDesc] = useState('');
    const [client, setClient] = useState('');
    const [technologies, setTechnologies] = useState('');
    const [projectFlow, setProjectFlow] = useState('');
    const [liveLink, setLiveLink] = useState('');
    const [videoLink, setVideoLink] = useState('');
    const [image, setImage] = useState('');
    const [moreImages, setMoreImages] = useState([]);
    const [uploadingMore, setUploadingMore] = useState(false);
    const [saving, setSaving] = useState(false);

    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        if (id) {
            const loadProject = async () => {
                try {
                    const { data } = await fetchProjectById(id);
                    setTitle(data.title || '');
                    setCategory(data.category || '');
                    setShortDesc(data.shortDesc || '');
                    setLongDesc(data.longDesc || '');
                    setClient(data.client || '');
                    setTechnologies(data.technologies ? data.technologies.join(', ') : '');
                    setProjectFlow(data.projectFlow ? data.projectFlow.join('\n') : '');
                    setLiveLink(data.liveLink || '');
                    setVideoLink(data.videoLink || '');
                    setImage(data.img || '');
                    setMoreImages(data.moreImages || []);
                } catch (error) {
                    console.error('Failed to fetch project details', error);
                }
            };
            loadProject();
        }
    }, [id]);

    const uploadMoreImagesHandler = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploadingMore(true);
        const uploadedUrls = [];
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('image', file);
                const data = await uploadFile(formData);
                const path = typeof data === 'string' ? data : (data.path || '');
                uploadedUrls.push(path.startsWith('/') ? path : `/${path}`);
            }
            setMoreImages([...moreImages, ...uploadedUrls]);
        } catch (error) {
            console.error('Gallery upload error', error);
        } finally {
            setUploadingMore(false);
        }
    };

    const removeImageHandler = (index) => {
        const newImages = [...moreImages];
        newImages.splice(index, 1);
        setMoreImages(newImages);
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        setSaving(true);
        let techArray = technologies ? technologies.split(',').map(t => t.trim()) : [];
        let flowArray = projectFlow ? projectFlow.split('\n').filter(s => s.trim() !== '') : [];

        const projectData = {
            title, category, shortDesc, longDesc, client,
            technologies: techArray, projectFlow: flowArray,
            liveLink, videoLink, img: image, moreImages
        };

        try {
            if (id) await updateProject(id, projectData);
            else await createProject(projectData);
            navigate('/admin/projects');
        } catch (error) {
            console.error('Failed to save project', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout title={id ? 'Edit Project' : 'Add New Project'}>
            <div className="bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">
                <form onSubmit={submitHandler} className="space-y-8">
                    {/* Basic Info */}
                    <div>
                        <h3 className="text-xl font-bold text-white mb-6 border-b border-gray-700 pb-2">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input
                                label="Project Title"
                                name="title"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Executive CRM Dashboard"
                            />
                            <Input
                                label="Category"
                                name="category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                placeholder="e.g. Web Architecture"
                            />
                        </div>
                    </div>

                    {/* Media */}
                    <div>
                        <h3 className="text-xl font-bold text-white mb-6 border-b border-gray-700 pb-2">Media & Assets</h3>
                        <FileUpload
                            label="Main Cover Image"
                            value={image}
                            onChange={(imgUrl) => setImage(imgUrl)}
                        />

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-400 mb-2">Gallery Screenshots</label>
                            <input
                                type="file"
                                multiple
                                className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-white hover:file:bg-gray-600 cursor-pointer"
                                onChange={uploadMoreImagesHandler}
                            />
                            {uploadingMore && <div className="text-indigo-400 text-sm mt-1 animate-pulse">Uploading gallery...</div>}
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
                                {moreImages.map((img, index) => (
                                    <div key={index} className="relative group rounded-md overflow-hidden border border-gray-700 bg-gray-900 h-24 aspect-square">
                                        <img src={getImageUrl(img)} alt={`Gallery ${index}`} className="w-full h-full object-cover" />
                                        <button type="button" className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 shadow-md" onClick={() => removeImageHandler(index)}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <h3 className="text-xl font-bold text-white mb-6 border-b border-gray-700 pb-2">Details & Specifications</h3>
                        <div className="space-y-6">
                            <TextArea
                                label="Short Description"
                                name="shortDesc"
                                required
                                rows={3}
                                value={shortDesc}
                                onChange={(e) => setShortDesc(e.target.value)}
                                placeholder="Brief overview of the project"
                            />
                            <TextArea
                                label="Long Description (HTML Allowed)"
                                name="longDesc"
                                required
                                rows={6}
                                value={longDesc}
                                onChange={(e) => setLongDesc(e.target.value)}
                                placeholder="Comprehensive project breakdown..."
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <Input
                                    label="Client / Company"
                                    name="client"
                                    value={client}
                                    onChange={(e) => setClient(e.target.value)}
                                    placeholder="e.g. Enterprise Global Inc."
                                />
                                <Input
                                    label="Technologies (Comma Separated)"
                                    name="technologies"
                                    value={technologies}
                                    onChange={(e) => setTechnologies(e.target.value)}
                                    placeholder="React, Node.js, PostgreSQL"
                                />
                            </div>

                            <TextArea
                                label="Project Flow (One Step Per Line)"
                                name="projectFlow"
                                rows={4}
                                value={projectFlow}
                                onChange={(e) => setProjectFlow(e.target.value)}
                                placeholder="Step 1: Requirement Gathering&#10;Step 2: Architecture Setup"
                            />
                        </div>
                    </div>

                    {/* Links */}
                    <div>
                        <h3 className="text-xl font-bold text-white mb-6 border-b border-gray-700 pb-2">Links & References</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input
                                label="Live Application URL"
                                name="liveLink"
                                icon={<i className="fas fa-link"></i>}
                                value={liveLink}
                                onChange={(e) => setLiveLink(e.target.value)}
                                placeholder="https://example.com"
                            />
                            <Input
                                label="Video Demo URL"
                                name="videoLink"
                                icon={<i className="fab fa-youtube"></i>}
                                value={videoLink}
                                onChange={(e) => setVideoLink(e.target.value)}
                                placeholder="https://youtube.com/watch?v=..."
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-700">
                        <Link to="/admin/projects" className="px-5 py-2.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 font-medium transition-colors">
                            Cancel
                        </Link>
                        <Button type="submit" variant="primary" isLoading={saving}>
                            {id ? 'Update Project' : 'Create Project'}
                        </Button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

export default ProjectForm;
